import { useState } from "react";
import { Rocket, Users, ShieldCheck, CheckCircle2 } from "lucide-react";

import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../../components/common/ToastMsg";
import { Button } from "../../../components/ui/button";
import { ConfirmPopupModal } from "../../../components/ui/dialogModal";
import { WizardFooter, PRIMARY_BTN } from "./WizardFooter";
import { EMPLOYEE_MIGRATION_PROCESS } from "../../../constants/api";

const MigrateStep = ({
    migrationId,
    counts,
    onMigrated,
    onCancel,
    processUrl = EMPLOYEE_MIGRATION_PROCESS,
    processExtra = {},
    entityLabel = "employees",
    migrateTitle = "Migrate employees?",
    migrateDescription,
    showEmployeeNotes = true,
    completed = false,
}) => {
    const { update } = useApiMethods();
    const [migrating, setMigrating] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const migrate = async () => {
        setConfirm(false);
        setMigrating(true);
        try {
            const res = await update(processUrl, {
                migration_id: migrationId,
                migrate: true,
                data: [],
                ...processExtra,
            });
            if (res?.status === "success") {
                ToastMsg("success", `${entityLabel} migrated successfully`);
                onMigrated();
            } else {
                ToastMsg("error", res?.message || "Migration failed");
            }
        } catch (e) {
            ToastMsg("error", "Something went wrong while migrating");
        } finally {
            setMigrating(false);
        }
    };

    return (
        <div>
            <div className="p-4 space-y-5">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {completed ? "Step 3 — Migration completed" : "Step 3 — Migrate"}
            </h3>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 max-w-xl">
                <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${completed ? "bg-green-100 dark:bg-green-900/40" : "bg-sidebar-primary/10"}`}>
                        {completed
                            ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                            : <Users className="w-5 h-5 text-sidebar-primary" />}
                    </div>
                    <div>
                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                            {counts.processed}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {completed ? `${entityLabel} migrated successfully` : `${entityLabel} ready to create`}
                        </p>
                    </div>
                </div>

                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                    {completed ? (
                        <li className="flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5" />
                            This migration batch has been completed. Records are now available in the system.
                        </li>
                    ) : (
                        <>
                            {showEmployeeNotes && (
                                <>
                                    <li className="flex items-start gap-2">
                                        <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5" />
                                        Employee records and their login accounts are created together — if any
                                        row fails, the whole batch is rolled back.
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5" />
                                        The migration is checked against your remaining subscription seats.
                                    </li>
                                </>
                            )}
                            {!showEmployeeNotes && (
                                <li className="flex items-start gap-2">
                                    <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5" />
                                    Records are created in a single transaction — if any row fails, the whole batch is rolled back.
                                </li>
                            )}
                        </>
                    )}
                </ul>
            </div>

            </div>

            <WizardFooter>
                <Button variant="outline" onClick={onCancel} disabled={migrating}>
                    {completed ? "Back to list" : "Cancel"}
                </Button>
                {!completed && (
                    <Button className={PRIMARY_BTN} onClick={() => setConfirm(true)} disabled={migrating || !counts.processed}>
                        <Rocket className="w-4 h-4 mr-1" />
                        {migrating ? "Migrating…" : "Migrate Now"}
                    </Button>
                )}
            </WizardFooter>

            {!completed && (
                <ConfirmPopupModal
                    open={confirm}
                    onClose={() => setConfirm(false)}
                    onConfirm={migrate}
                    type="info"
                    title={migrateTitle}
                    description={migrateDescription || `This will create ${counts.processed} ${entityLabel}. This cannot be undone.`}
                    confirmLabel="Migrate"
                />
            )}
        </div>
    );
};

export default MigrateStep;
